import { useQueries, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  CompetitionPrize,
  UserRankWithAvg,
  fetchCompetition,
  fetchCompetitionPrizes,
  fetchCompetitionSummary,
  fetchRanking,
} from '../../api/rank'


function prizesToMap(prizes: CompetitionPrize[]) {
  const prizeMap = new Map()

  for (const prize of prizes) {
    prizeMap.set(prize.rank, prize.prizeName)
  }

  return prizeMap
}


export const useFetchCompetition = () => {
  const { competitionID } = useParams()
  const id = Number(competitionID)
  const [competition, summary] = useQueries({
    queries: [
      {
        queryKey: ['useFetchCompetition', id],
        queryFn: () => fetchCompetition(id),
      },
      {
        queryKey: ['useFetchCompetitionSummary', id],
        queryFn: () => fetchCompetitionSummary(id),
        refetchInterval: 60000,
      },
      {
        queryKey: ['useFetchCompetitionPrizes', id],
        queryFn: () => fetchCompetitionPrizes(id),
      },
    ],
  })

  const totalTrial = useMemo(() => {
    if (!summary.data) {
      return 0
    }

    const { playCnt, giveupCnt } = summary.data

    return Number(playCnt) + Number(giveupCnt)
  }, [summary.data])

  return {
    ...competition,
    totalTrial,
  }
}

export const useFetchPrizes = () => {
  const { competitionID } = useParams()
  const id = Number(competitionID)
  const { data, ...rest } = useQuery({
    queryKey: ['useFetchCompetitionPrizes', id],
    queryFn: () => fetchCompetitionPrizes(id),
  })

  return {
    ...rest,
    prizes: prizesToMap(data || []),
  }
}

export const useFetchRank = () => {
  const { competitionID } = useParams()

  return useQuery({
    queryKey: ['useFetchRank', competitionID],
    queryFn: () =>
      fetchRanking({
        competitionID: Number(competitionID),
        page: 1,
        limit: 50,
      }),
    retry: false,
  })
}

export const useRankerList = () => {
  const { data, refetch, dataUpdatedAt } = useFetchRank()
  const [rankers, setRankers] = useState<UserRankWithAvg[]>([])

  useEffect(() => {
    let userRanks: UserRankWithAvg[] = []
    let page = 1
    let index = 0

    if (data) {
      userRanks = data.userRanks
      // 최대 5페이지까지만 보여준다.
      page = data.totalCount > 50 ? 5 : Math.ceil(data.totalCount / 10)
    }

    // 랭킹페이지가 바뀔 때 0.6초간 빈 화면 보여주고
    // 10명의 랭커를 선정.
    const setRankerList = async () => {
      setRankers([])

      const start = index * 10
      const end = start + 10

      setTimeout(() => {
        setRankers(userRanks.slice(start, end))
        index++
      }, 600)
    }

    // 순위를 5초 마다 10개씩 보여주고
    // 모든 순위를 보여줬으면 랭킹 리스트를 다시 호출함.
    setRankerList()
    const interval = setInterval(() => {
      setRankerList()

      if (index >= page) {
        refetch()

        return
      }
    }, 10000)

    return () => {
      clearInterval(interval)
    }
  }, [data, refetch, dataUpdatedAt])

  return rankers
}
